import { useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminQueries } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { Label, SearchInput, Select } from '@blimo/ui';

/**
 * Person picker (e.g. Ableser): search via GET /admin/parties?filter[q]=… (ab 2 Zeichen), Auswahl aus den Treffern.
 * The current value stays selectable with its known name.
 * @param {{
 *   label: string,
 *   value?: string,
 *   valueLabel?: string|null,
 *   onChange: (id: string, party?: { id: number, display_name?: string|null }) => void,
 *   emptyLabel?: string,
 *   size?: 'default'|'sm',
 *   roleFilter?: string,
 *   typeFilter?: 'person'|'organization',
 * }} props
 */
export function PartyPicker({ label, value, valueLabel, onChange, emptyLabel = 'Keine Auswahl', size = 'default', roleFilter, typeFilter }) {
  const api = useApi();
  const id = useId();
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState(/** @type {{ id: string, label: string }|null} */ (null));
  const enabled = q.trim().length >= 2;
  const { data, isFetching } = useQuery({
    ...adminQueries.parties(api, { per_page: 20, filter: { q: q.trim(), role: roleFilter, type: typeFilter } }),
    enabled,
  });
  const results = enabled ? (data?.data ?? []) : [];
  const options = results.map((p) => ({ value: String(p.id), label: p.display_name || `#${p.id}`, party: p }));
  if (value && !options.some((o) => o.value === value)) {
    const known = picked?.id === value ? picked.label : valueLabel;
    options.unshift({ value, label: known || `Person #${value}`, party: undefined });
  }

  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id} className={size === 'sm' ? 'text-xs text-muted-foreground' : undefined}>
        {label}
      </Label>
      <div className="flex flex-col gap-1 sm:flex-row">
        <SearchInput
          label={`${label}: Person suchen`}
          placeholder="Name suchen …"
          value={q}
          onValueChange={setQ}
          wrapperClassName={size === 'sm' ? 'sm:w-40' : 'sm:w-48'}
        />
        <Select
          id={id}
          size={size}
          value={value ?? ''}
          onChange={(e) => {
            const option = options.find((o) => o.value === e.target.value);
            if (option) setPicked({ id: option.value, label: option.label });
            onChange(e.target.value, option?.party);
          }}
          wrapperClassName={size === 'sm' ? 'sm:w-44' : 'sm:flex-1'}
          aria-busy={isFetching || undefined}
        >
          <option value="">{emptyLabel}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </div>
      {enabled && !isFetching && results.length === 0 && <span className="text-xs text-muted-foreground">Keine Person gefunden.</span>}
    </div>
  );
}
